import { faCopy } from '@fortawesome/free-solid-svg-icons';
import * as React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { connect } from 'react-redux';
import { EventType, UserViewModel } from 'stageclip-common';

import * as DateHelpers from '../../helpers/dateHelpers';
import * as NotificationHelper from '../../helpers/notificationHelper';
import * as PermissionsHelper from '../../helpers/permissionsHelper';
import { NormalizedClient } from '../../models/normalized/normalizedClient';
import { NormalizedEvent } from '../../models/normalized/normalizedEvent';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import { getItem } from '../../store/selectors';
import Button from '../buttons/button';
import { baseMapDispatchToProps, baseMapStateToProps, ComponentBase, BaseStateProps } from '../componentBase';
import { FullName } from '../users/fullName';
// eslint-disable-next-line import/no-named-default
import { default as InfoGroup, InfoGroupRowData } from '../utils/infoGroup';

import styles from './eventDetails.module.scss';

interface EventDetailsOwnProps {
  event: NormalizedEvent;
}

interface EventDetailsStateProps extends BaseStateProps {
  client: NormalizedClient;
  assignee: UserViewModel;
}

class EventDetails extends ComponentBase<EventDetailsOwnProps, EventDetailsStateProps> {
  private static copyToClipboardOnClick() {
    NotificationHelper.success('Path copied to clipboard');
  }

  protected loggedInRender() {
    return (
      <>
        <div className={styles.details}>
          <InfoGroup title="General Info" data={this.getGeneralInfoData()} />
          {PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.ViewEventStorageDetails) && (
            <InfoGroup title="Azure Storage Details" data={this.getStorageDetailsData()} />
          )}
        </div>
      </>
    );
  }

  private getGeneralInfoData(): InfoGroupRowData[] {
    const { event } = this.props;

    const data: InfoGroupRowData[] = [
      { name: 'Type', subRows: [event.type, event.isSelfClipping && '(Grad Customization - ON)'] },
      { name: 'Status', subRows: [event.state] },
    ];

    if (event.assignee) {
      data.push({ name: 'Assignee', subRows: [<FullName key={this.props.assignee.id} user={this.props.assignee} />] });
    }

    if (PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.ViewEventPrice)) {
      data.push({ name: 'Price', subRows: [this.props.event.clipPrice] });
    }

    if (
      PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.ViewParticipantsUploadDate) &&
      this.props.event.participantsLastUploadedAt
    ) {
      data.push({
        name: 'Participants last upload time ',
        subRows: [`User: ${DateHelpers.readableDateFormat(this.props.event.participantsLastUploadedAt)}`],
      });
    }

    if (event.type === EventType.LiveEvent) {
      data.push({
        name: 'Template status',
        subRows: [
          // eslint-disable-next-line no-nested-ternary
          this.props.event.eventConfiguration
            ? this.props.event.eventConfiguration.isLiveTemplateFinalize
              ? 'Clip template has been finalized.'
              : 'Clip template has not been finalized.'
            : 'Clip template not selected',
        ],
      });
    }

    data.push({ name: 'Notes', subRows: [this.props.event.notes || '-'] });

    return data;
  }

  private getStorageDetailsData(): InfoGroupRowData[] {
    return [
      { name: 'Account name', subRows: [this.props.event.storageDetails.storageAccountName] },
      { name: 'Container name', subRows: [this.props.event.storageDetails.containerName] },
      {
        name: 'Folder',
        subRows: [
          <CopyToClipboard text={this.props.event.storageDetails.folderPath} key={0}>
            <Button
              className={styles.copy}
              onClick={EventDetails.copyToClipboardOnClick}
              text="Copy folder path to clipboard"
              allCapitals
              icon={faCopy}
            />
          </CopyToClipboard>,
        ],
      },
    ];
  }
}

function mapStateToProps(state: ClippingPlatformState, ownProps: EventDetailsOwnProps): EventDetailsStateProps {
  return {
    ...baseMapStateToProps(state),
    assignee: getItem(state.entities.users, ownProps.event.assignee),
    client: getItem(state.entities.clients, ownProps.event.client),
  };
}

export default connect(mapStateToProps, baseMapDispatchToProps)(EventDetails);
